import {
  EllipsisSpan,
  Table,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
  Button,
  Form,
  CustomFilter,
  useUpdate,
  DatePicker,
  Tabs,
  Tag,
  MultiSelect,
  Select,
  Tooltip,
} from '@maxtropy/components';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { ResizeCallbackData } from 'react-resizable';
import styles from '../index.module.scss';
import {
  apiV2EnergyConsumptionPlanEnergyDispatchOrderPost,
  V2EnergyConsumptionPlanEnergyDispatchOrderPostResponse,
  apiV2AreaList2Post,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import ResizableTitle from './ResizableTitle';
import { formatTimeRange } from '../utils';
const { RangePicker } = DatePicker;

type FilterParams = Omit<any, 'page' | 'size'>;

const getWeekRange = (date: dayjs.Dayjs) => {
  const day = date.day();
  const diffToSat = (day + 1) % 7;
  const start = date.startOf('day').subtract(diffToSat, 'day');
  const end = start.add(6, 'day');
  return [start, end];
};

// 生成区域的字符串标识，用于比较是否相同（区域内合并）
const getAreaKey = (areaList: TimeDataProps['areaList']) => {
  if (!areaList || areaList.length === 0) return '';
  return areaList
    .map(item => item.id)
    .sort((a, b) => (a || 0) - (b || 0))
    .join(',');
};

// 生成介质+阀门的字符串集合标识，用于区域间合并（区域内所有行的介质+阀门字符串集合）
const getMediumValveKey = (mediumValveList: TimeDataProps['mediumValveList']) => {
  return (
    mediumValveList
      ?.map(item => `${item.mediumName || ''}-${item.deviceName || ''}`)
      .sort()
      .join('|') || ''
  );
};

type TimeDataProps = Exclude<V2EnergyConsumptionPlanEnergyDispatchOrderPostResponse['list'], undefined>[number];

const EnergyGovernance: FC = () => {
  const [form] = Form.useForm();
  const [update, setUpdate] = useUpdate();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<V2EnergyConsumptionPlanEnergyDispatchOrderPostResponse['list']>([]);
  // 区域列的 rowSpan（区域内合并 + 区域间合并）
  const [rowSpans, setRowSpans] = useState<Record<number, { span: number; mergedZones: string[] }>>({});
  // 介质列的 rowSpan（区域内合并，仅检查当前区域内的行）
  // const [mediumRowSpans, setMediumRowSpans] = useState<Record<number, { span: number; mediumList: number[] }>>({});
  // 时间列的 rowSpan（区域内合并 + 区域间合并，用于显示所有合并行的时间数据）
  // const [timeRowSpans, setTimeRowSpans] = useState<Record<number, { span: number; timeIndices: number[] }>>({});
  const [rangeDate, setRangeDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [searchParams, setSearchParams] = useState<FilterParams>({});
  const [open, setOpen] = useState(false);
  const [buildColumns, setBuildColumns] = useState<any[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const { data: areaList } = useRequest(() => apiV2AreaList2Post().then(res => res.list ?? []));

  const now = dayjs();

  const [currentPeriodStart, currentPeriodEnd] = getWeekRange(now);

  useEffect(() => {
    // 日期初始化
    const defaultStart = currentPeriodStart;
    const defaultEnd = currentPeriodEnd;
    setRangeDate([defaultStart, defaultEnd]);
  }, []);

  useEffect(() => {
    setLoading(true);
    if (!rangeDate) return;
    apiV2EnergyConsumptionPlanEnergyDispatchOrderPost({
      ...searchParams,
      startDate: rangeDate ? rangeDate[0]?.valueOf() : undefined,
      endDate: rangeDate ? rangeDate[1]?.valueOf() : undefined,
    })
      .then(res => {
        // 先把数据设置到 state
        setData(res.list);
        const calcRowSpans = (list: any[]) => {
          const map: Record<number, { span: number; mergedZones: string[] }> = {};
          let i = 0;

          while (i < list.length) {
            // 1. 找出从当前位置开始，所有连续且属于同一个区域的行
            const currentAreaKey = getAreaKey(list[i]?.areaList);
            const areaGroup: number[] = [i];

            for (let j = i + 1; j < list.length; j++) {
              // 只要区域 Key 相同，就归为一组
              if (getAreaKey(list[j]?.areaList) === currentAreaKey) {
                areaGroup.push(j);
              } else {
                break; // 遇到不同区域，停止查找
              }
            }

            // 2. 收集当前这一组（同一个区域）内的所有区域名
            const currentZoneNames = new Set<string>();
            areaGroup.forEach(idx => {
              list[idx]?.areaList?.forEach((area: any) => {
                currentZoneNames.add(area.zoneName);
              });
            });
            const mergedZones = Array.from(currentZoneNames);

            // 3. 设置 RowSpan
            // 这里的 span 仅仅是当前区域组的长度，不再向后延伸
            map[i] = { span: areaGroup.length, mergedZones };

            // 4. 将组内剩余的行设为 0（被合并）
            for (let k = 1; k < areaGroup.length; k++) {
              map[i + k] = { span: 0, mergedZones: [] };
            }

            // 5. 指针移动到下一个区域的开始处
            i += areaGroup.length;
          }

          return map;
        };

        // 先计算区域列的 rowSpans
        const rowSpanMap = calcRowSpans(res.list || []);
        setRowSpans(rowSpanMap);
      })
      .finally(() => setLoading(false));
  }, [searchParams, rangeDate, update]);

  // 动态生成列；当 rowSpans 更新（来自数据）时也需要重新生成列以读取最新的 rowSpan
  useEffect(() => {
    if (!rangeDate) return;
    const [start, end] = rangeDate;
    const newColumns = generateColumns(start, end);
    setBuildColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rowSpans, rangeDate]);

  const generateColumns = (start: Dayjs, end: Dayjs) => {
    const cols: any[] = [
      {
        title: '站房管道来源区域',
        width: columnWidths['areaList'] || 160,
        dataIndex: 'areaList',
        onCell: (record: TimeDataProps, index: number | undefined) => {
          if (index === undefined) return {};
          const rowspan = rowSpans[index]?.span;
          if (rowspan === 0) {
            return { rowSpan: 0 };
          }
          return { rowSpan: rowspan };
        },
        render: (v: TimeDataProps['areaList'], record: TimeDataProps, index: number) => {
          // 如果该行被合并（rowspan === 0），则不显示内容
          if (rowSpans[index]?.span === 0) return null;
          const mergedZones = rowSpans[index]?.mergedZones;
          if (mergedZones && mergedZones.length > 0) {
            return <EllipsisSpan value={mergedZones.join(', ')} />;
          }
          if (!v || v.length === 0) return '-';
          return <EllipsisSpan value={v.map(item => item.zoneName).join(', ')} />;
        },
      },
      {
        title: '供气管道介质',
        width: columnWidths['mediumValveList'] || 160,
        dataIndex: 'mediumValveList',
        render: (_: any, _record: any, index: number) => {
          const mediumValveList = _record?.mediumValveList || [];
          return (
            <div>
              {mediumValveList.map((mv: any, idx: number) => (
                <Tooltip key={idx} title={mv.deviceName}>
                  <Tag style={{ marginBlock: 4 }}>{mv.mediumName}</Tag>
                </Tooltip>
              ))}
            </div>
          );
        },
      },
    ];

    for (let i = 0; i < 7; i++) {
      const dayIndex = i; // 创建独立变量确保闭包正确捕获
      const day = start.add(i, 'day');
      const dateStr = day.format('YYYY-MM-DD');
      const dataIndex = `day${i + 1}Data`;
      cols.push({
        title: dateStr,
        dataIndex,
        width: columnWidths[dataIndex] || 160,
        render: (_: any, _record: TimeDataProps, index: number) => {
          const dayDetail = _record?.dayTimeDetailList?.[dayIndex];
          const timeSlotList = dayDetail?.timeSlotList || [];
          if (timeSlotList.length === 0) return '-';
          const timeStrs = timeSlotList.map(slot => formatTimeRange(dateStr, slot?.startDateTime, slot?.endDateTime));
          return <span>{timeStrs.join('、')}</span>;
        },
      });
    }
    return cols;
  };

  const areaOptions = useMemo(() => areaList?.map(item => ({ label: item.zoneName, value: item.id })), [areaList]);

  const handleResize = useCallback(
    (dataIndex: string) =>
      (_: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setColumnWidths(prev => ({
          ...prev,
          [dataIndex]: size.width,
        }));
      },
    []
  );

  const mergedColumns = useMemo(
    () =>
      buildColumns.map(col => ({
        ...col,
        width: columnWidths[col.dataIndex] || col.width,
        onHeaderCell: (column: any) => ({
          width: columnWidths[column.dataIndex] || column.width,
          onResize: handleResize(column.dataIndex),
        }),
      })),
    [buildColumns, columnWidths, handleResize]
  );

  const handleDateSelect = (date: dayjs.Dayjs) => {
    const [start, end] = getWeekRange(date);
    setRangeDate([start, end]);
    setOpen(false);
  };

  const onFinish = (val: FilterParams) => {
    setSearchParams(val);
  };

  const onReset = () => {
    // 日期初始化
    const defaultStart = currentPeriodStart;
    const defaultEnd = currentPeriodEnd;
    setRangeDate([defaultStart, defaultEnd]);
    setSearchParams({});
  };

  return (
    <>
      <CustomFilter<FilterParams> form={form} onFinish={val => onFinish(val as FilterParams)} onReset={onReset}>
        <Form.Item name="areaId" label="区域">
          <Select
            showSearch
            allowClear
            placeholder="请选择"
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={areaOptions}
          />
        </Form.Item>
        <Form.Item label="日期范围">
          <RangePicker
            open={open}
            value={rangeDate}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            placeholder={['开始日期', '结束日期']}
            // disabledDate={disabledDate}
            onOpenChange={nextOpen => {
              setOpen(nextOpen);
            }}
            onCalendarChange={dates => {
              if (dates && dates[0]) {
                handleDateSelect(dates[0]); // 确保 dates[0] 不为空
              }
            }}
          />
        </Form.Item>
      </CustomFilter>

      <Table
        rowKey="id"
        sticky
        bordered
        loading={loading}
        dataSource={data}
        className={styles.governTable}
        columns={mergedColumns}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

export default EnergyGovernance;
